import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import DadosEmpresa from "../components/DadosEmpresa";
import LinkVagasEmpresa from "../components/LinkVagasEmpresa";
import { obterConta, salvarConta, salvarPreferencias, obterEmpresa, salvarEmpresaPerfil, trocarSenha, enviarLogoEmpresa, empresaLogoUrl,
  modelosEncerramento, criarModeloEncerramento, salvarModeloEncerramento, excluirModeloEncerramento,
  motivosEncerramento, criarMotivoEncerramento, excluirMotivoEncerramento, obterConfigEmpresa,
  TIPOS_MODELO_ENCERRAMENTO, MOTIVOS_ENCERRAMENTO_PADRAO, TAGS_ENCERRAMENTO, vagasLumeStatus, vagasLumeDesconectar } from "../services/api";
import { HtmlEditor, WhatsappEditor } from "../components/RichEditors";

const PORTES = ["Pequena", "Média", "Grande"];

export default function ContaPage() {
  const { user, setPrefs, atualizar } = useAuth();
  const [conta, setConta] = useState(null);
  const [emp, setEmp] = useState(null);
  const [msg, setMsg] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [logoV, setLogoV] = useState(0);        // cache-buster p/ recarregar a imagem após trocar
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    if (!user) { setCarregando(false); return; }
    (async () => {
      try {
        const c = await obterConta();
        setConta({ nome: c.nome, email: c.email, whatsapp: c.whatsapp || "", notificarEmail: c.notificarEmail, notificarWhatsapp: c.notificarWhatsapp, assinaturaOutreach: c.assinaturaOutreach || "", receberVagas: c.receberVagas !== false });
        if (c.tipo === 1 && c.empresaId) {
          const e = await obterEmpresa(c.empresaId);
          // O e-mail do responsável por candidaturas NÃO vem no DTO público da empresa (é dado operacional):
          // busca à parte, e falha aqui não pode derrubar a tela toda.
          let cfg = {};
          try { cfg = await obterConfigEmpresa(); } catch { /* segue sem o campo preenchido */ }
          setEmp({ id: c.empresaId, nome: e.empresa.nome, setor: e.empresa.setor || "", cidade: e.empresa.cidade || "", sobre: e.empresa.sobre || "", porte: e.empresa.porte || "", verificada: e.empresa.verificada, temLogo: e.empresa.temLogo, certificacoes: (e.empresa.certificacoes || []).join(", "), emailCandidaturas: cfg.emailCandidaturas || "" });
        }
      } catch (e) { setErro(e.message); } finally { setCarregando(false); }
    })();
  }, [user]);

  if (!user) return <Tela><Aviso>Faça <Link to="/entrar" className="text-primary font-semibold">login</Link> para acessar sua conta.</Aviso></Tela>;
  if (carregando || !conta) return <Tela><p className="text-gray-500">Carregando...</p></Tela>;

  const salvar = async () => {
    setMsg(null); setErro(null);
    try {
      await salvarConta(conta.nome, conta.whatsapp, conta.assinaturaOutreach);
      await salvarPreferencias(conta.notificarEmail, conta.notificarWhatsapp, conta.receberVagas);
      setPrefs?.(conta.notificarEmail, conta.notificarWhatsapp);
      atualizar?.({ nome: conta.nome.trim() }); // sincroniza o nome no contexto (Header/saudação/AutorNome) — evita defasagem
      setConta((c) => ({ ...c, nome: c.nome.trim() }));
      // Só tenta salvar a página da empresa se ela já está VERIFICADA (mesmo gate do backend). Sem isto, uma empresa
      // em análise recebia o erro "em análise" do salvarEmpresaPerfil, que MASCARAVA o sucesso dos dados pessoais.
      // A página da empresa tem botão próprio (DadosEmpresa) — salvar as duas coisas num botão só fazia
      // o erro "empresa em análise" mascarar o SUCESSO dos dados pessoais.
      setMsg("Conta atualizada!");
    } catch (e) { setErro(e.message); }
  };

  const enviarLogo = async (file) => {
    if (!file) return;
    setMsg(null); setErro(null); setLogoBusy(true);
    try {
      await enviarLogoEmpresa(file);
      setEmp((x) => ({ ...x, temLogo: true }));
      setLogoV((v) => v + 1); // força o browser a recarregar a imagem nova
      setMsg("Logo atualizado!");
    } catch (e) { setErro(e.message); } finally { setLogoBusy(false); }
  };

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  return (
    <Tela>
      <h1 className="text-2xl md:text-3xl font-bold text-text">Minha conta</h1>
      <p className="text-gray-600 text-sm mt-1">{user.tipo === 1 ? "Dados da sua empresa e preferências." : "Seus dados e preferências."}</p>
      {msg && <p role="status" className="mt-3 rounded-lg bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm">{msg}</p>}
      {erro && <p role="alert" className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">{erro}</p>}

      {/* Dados pessoais */}
      <Bloco titulo="Dados de acesso">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><label className={labelCls}>Nome</label>
            <input className={inputCls} aria-label="Nome" value={conta.nome} onChange={(e) => setConta({ ...conta, nome: e.target.value })} /></div>
          <div><label className={labelCls}>E-mail</label>
            <input className={`${inputCls} bg-gray-50`} aria-label="E-mail (não editável)" value={conta.email} disabled /></div>
          <div><label className={labelCls}>WhatsApp</label>
            <input className={inputCls} aria-label="WhatsApp" value={conta.whatsapp} onChange={(e) => setConta({ ...conta, whatsapp: e.target.value })} placeholder="(11) 99999-9999" /></div>
        </div>
        {emp && (
          <div className="mt-3">
            <label className={labelCls}>Assinatura de abordagem <span className="font-normal text-gray-400">— entra no fim das mensagens que você envia aos candidatos (e-mail e WhatsApp)</span></label>
            <textarea className={inputCls} aria-label="Assinatura de abordagem" rows="2" maxLength={600}
              value={conta.assinaturaOutreach} onChange={(e) => setConta({ ...conta, assinaturaOutreach: e.target.value })}
              placeholder={`— ${conta.nome || "Seu nome"}, Recrutador(a)${emp ? " na " + emp.nome : ""}`} />
            <p className="mt-1 text-[11px] text-gray-400">Ex.: <em>— {conta.nome || "Ana Souza"}, Talent Acquisition{emp ? " na " + emp.nome : ""} · linkedin.com/in/voce</em></p>
          </div>
        )}
      </Bloco>

      {/* Empresa (se for conta de empresa) — MESMO componente usado no painel. Este formulário existia aqui
          duplicado; na primeira vez que um campo mudasse, uma das duas telas ficaria para trás. Ele carrega e
          salva por conta própria, então o botão "Salvar" lá embaixo cuida só dos dados pessoais. */}
      {emp && <div className="mt-5"><DadosEmpresa titulo="Página da empresa" /></div>}
      {emp && <div className="mt-5"><LinkVagasEmpresa /></div>}

      {/* Mensagens de encerramento: a empresa escreve uma vez, com a voz dela, e reusa em toda vaga que fechar. */}
      {emp && emp.verificada && <ModelosEncerramento />}

      {/* Trocar senha */}
      <TrocarSenhaBloco />

      {/* Consentimento LGPD */}
      <Bloco titulo="Quero receber notificações (LGPD)">
        <p className="text-sm text-gray-600 mb-2">
          Você escolhe por onde quer ser avisado. Toda conversa acontece <strong>no portal</strong> — a outra parte nunca vê seu e-mail/telefone.
        </p>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={conta.notificarEmail} onChange={(e) => setConta({ ...conta, notificarEmail: e.target.checked })} /> Receber por e-mail
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700 mt-1">
          <input type="checkbox" checked={conta.notificarWhatsapp} onChange={(e) => setConta({ ...conta, notificarWhatsapp: e.target.checked })} /> Receber por WhatsApp
        </label>
        {/* CONTROLE PRÓPRIO para vaga. Antes era tudo ou nada: quem só queria parar de receber oferta desligava
            o e-mail inteiro e perdia junto o aviso de que a empresa respondeu a candidatura dele — justamente o
            que ele veio buscar aqui. Só aparece para candidato: empresa não recebe alerta de vaga. */}
        {user.tipo === 0 && (
          <>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-3">
              <input type="checkbox" checked={conta.receberVagas !== false}
                onChange={(e) => setConta({ ...conta, receberVagas: e.target.checked })} />
              Quero ser avisado de vagas que combinam com meu currículo
            </label>
            <p className="text-[11px] text-gray-400 ml-6">
              Desmarcar para de enviar vagas novas. Você continua recebendo o retorno das candidaturas que já fez.
            </p>
          </>
        )}
        {/* Consentimento sem número = silêncio total, e a pessoa nunca descobre. O aviso evita isso. */}
        {conta.notificarWhatsapp && (conta.whatsapp || "").replace(/\D/g, "").length < 10 && (
          <p className="text-[11px] text-amber-700 mt-1 ml-6">
            Preencha o campo <strong>WhatsApp</strong> acima (com DDD) — sem número, o aviso por WhatsApp não sai.
          </p>
        )}
      </Bloco>

      {/* 🔗 Integração VagasLume (20/10): só aparece se a pessoa conectou. Desconectar aqui avisa o parceiro e corta os avisos. */}
      {user.tipo === 0 && <IntegracoesConta />}

      <div className="mt-5 flex gap-3 items-center">
        <button onClick={salvar} className="rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-2.5">Salvar</button>
        {user.tipo === 0 && <Link to="/curriculo" className="text-sm text-primary font-semibold hover:underline">Editar meu currículo →</Link>}
        {user.tipo === 1 && <Link to="/painel" className="text-sm text-primary font-semibold hover:underline">Ir para o painel →</Link>}
      </div>
    </Tela>
  );
}

function Tela({ children }) {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8 pb-28 md:pb-8">{children}</main>
    </div>
  );
}
function Aviso({ children }) { return <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-600">{children}</div>; }
function Bloco({ titulo, children }) {
  return (
    <section className="mt-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="font-bold text-gray-900 mb-3">{titulo}</h2>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------------------------------
// Mensagens de encerramento de vaga — 3 abas (Aprovado · Não aprovados · Sem contratação).
// A empresa escreve com a voz dela, marca um padrão por aba e reusa em toda vaga que fechar.
// ---------------------------------------------------------------------------------------------------
function ModelosEncerramento() {
  const [tipo, setTipo] = useState(0);
  const [modelos, setModelos] = useState(null);
  const [motivos, setMotivos] = useState([]);
  const [edit, setEdit] = useState(null); // { id?, nome, corpo }
  const [erro, setErro] = useState(null);
  const [ok, setOk] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    modelosEncerramento().then((m) => setModelos(m || [])).catch(() => setModelos([]));
    motivosEncerramento().then((m) => setMotivos(m || [])).catch(() => setMotivos([]));
  }, []);

  const flash = (m) => { setOk(m); setTimeout(() => setOk(null), 3000); };
  const doTipo = (modelos || []).filter((m) => m.tipo === tipo);
  const aba = TIPOS_MODELO_ENCERRAMENTO[tipo];
  // {motivo} só faz sentido em "Sem contratação" — nas outras abas ela não teria o que substituir.
  const tags = tipo === 2 ? [...TAGS_ENCERRAMENTO, "{motivo}"] : TAGS_ENCERRAMENTO;

  const salvar = async () => {
    if (!edit?.nome?.trim() || !edit?.corpo?.trim()) { setErro("Dê um nome ao modelo e escreva a mensagem."); return; }
    setBusy(true); setErro(null);
    try {
      const dados = { tipo, nome: edit.nome, corpo: edit.corpo, corpoWhatsapp: edit.corpoWhatsapp || null, padrao: edit.padrao };
      if (edit.id) {
        const m = await salvarModeloEncerramento(edit.id, dados);
        setModelos((ms) => ms.map((x) => (x.id === m.id ? m : (m.padrao && x.tipo === tipo ? { ...x, padrao: false } : x))));
      } else {
        const m = await criarModeloEncerramento(dados);
        setModelos((ms) => [...(m.padrao ? ms.map((x) => (x.tipo === tipo ? { ...x, padrao: false } : x)) : ms), m]);
      }
      setEdit(null); flash("Modelo salvo ✓");
    } catch (e) { setErro(e.message); }
    setBusy(false);
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir este modelo?")) return;
    try {
      await excluirModeloEncerramento(id);
      const restantes = (modelos || []).filter((m) => m.id !== id);
      // Espelha a promoção que o backend faz: o tipo nunca fica sem padrão.
      const doMesmo = restantes.filter((m) => m.tipo === tipo);
      if (doMesmo.length > 0 && !doMesmo.some((m) => m.padrao)) doMesmo[0].padrao = true;
      setModelos(restantes); flash("Modelo excluído.");
    } catch (e) { setErro(e.message); }
  };

  const tornarPadrao = async (m) => {
    try {
      const salvo = await salvarModeloEncerramento(m.id, { tipo: m.tipo, nome: m.nome, corpo: m.corpo, corpoWhatsapp: m.corpoWhatsapp, padrao: true });
      setModelos((ms) => ms.map((x) => (x.tipo === m.tipo ? { ...x, padrao: x.id === salvo.id } : x)));
      flash("Agora é o padrão desta aba ✓");
    } catch (e) { setErro(e.message); }
  };

  const inp = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";
  return (
    <Bloco titulo="Mensagens de encerramento de vaga">
      <p className="text-xs text-gray-500 -mt-2 mb-3">
        Escreva com a voz da sua empresa uma vez e reuse em toda vaga que fechar. O modelo marcado como <strong>padrão</strong> já
        vem preenchido na hora de encerrar — e você ainda pode ajustar antes de enviar.
      </p>

      <div className="flex flex-wrap gap-1 border-b border-gray-200">
        {TIPOS_MODELO_ENCERRAMENTO.map((t) => (
          <button key={t.v} onClick={() => { setTipo(t.v); setEdit(null); }}
            className={`px-3 py-2 text-sm font-semibold rounded-t-lg ${tipo === t.v ? "bg-primary text-white" : "text-gray-600 hover:bg-gray-100"}`}>
            {t.t}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-2">{aba.ajuda}</p>

      {modelos === null ? <p className="mt-3 text-sm text-gray-400">Carregando…</p> : (
        <div className="mt-3 space-y-2">
          {doTipo.length === 0 && <p className="text-sm text-gray-500">Nenhum modelo nesta aba ainda — usamos o texto padrão do eaiRH até você criar o seu.</p>}
          {doTipo.map((m) => (
            <div key={m.id} className="rounded-xl border border-gray-200 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-semibold text-sm text-gray-800">
                  {m.nome} {m.padrao && <span className="ml-1 rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[11px]">padrão</span>}
                </span>
                <div className="flex gap-3 text-xs font-semibold">
                  {!m.padrao && <button onClick={() => tornarPadrao(m)} className="text-primary hover:underline">Tornar padrão</button>}
                  <button onClick={() => setEdit({ ...m })} className="text-gray-600 hover:underline">Editar</button>
                  <button onClick={() => excluir(m.id)} className="text-red-600 hover:underline">Excluir</button>
                </div>
              </div>
              <div className="mt-1 text-xs text-gray-500 [&_p]:m-0" dangerouslySetInnerHTML={{ __html: m.corpo }} />
            </div>
          ))}

          {edit ? (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 space-y-3">
              <input className={inp} aria-label="Nome do modelo" placeholder="Nome do modelo (ex.: Padrão carinhoso)"
                value={edit.nome} onChange={(e) => setEdit({ ...edit, nome: e.target.value })} />
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">✉️ Versão e-mail</label>
                <HtmlEditor key={`html-${edit.id || "novo"}-${tipo}`} initialHtml={edit.corpo}
                  onChange={(html) => setEdit((p) => ({ ...p, corpo: html }))} tags={tags} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">💬 Versão WhatsApp <span className="font-normal text-gray-400">(opcional)</span></label>
                <WhatsappEditor value={edit.corpoWhatsapp || ""} onChange={(v) => setEdit((p) => ({ ...p, corpoWhatsapp: v }))}
                  tags={tags} rows={5} placeholder="Oi, {nome}! ..." />
                <p className="text-[11px] text-gray-400 mt-1">Deixe em branco e a gente manda o mesmo recado do e-mail, em texto simples.</p>
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input type="checkbox" checked={!!edit.padrao} onChange={(e) => setEdit({ ...edit, padrao: e.target.checked })} />
                Usar como padrão desta aba
              </label>
              <div className="flex gap-2">
                <button onClick={salvar} disabled={busy} className="rounded-lg bg-primary disabled:opacity-60 text-white font-semibold px-4 py-1.5 text-sm">{busy ? "Salvando…" : "Salvar modelo"}</button>
                <button onClick={() => setEdit(null)} className="rounded-lg border border-gray-300 text-gray-600 font-semibold px-3 py-1.5 text-sm hover:bg-gray-50">Cancelar</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setEdit({ nome: "", corpo: "", padrao: doTipo.length === 0 })}
              className="text-sm font-semibold text-primary hover:underline">+ Novo modelo</button>
          )}
        </div>
      )}

      {/* Motivos só fazem sentido na aba "Sem contratação" — são eles que preenchem a tag {motivo}. */}
      {tipo === 2 && <MotivosEncerramento motivos={motivos} setMotivos={setMotivos} onErro={setErro} onOk={flash} inp={inp} />}

      {erro && <p role="alert" className="mt-2 text-sm text-red-600">{erro}</p>}
      {ok && <p role="status" className="mt-2 text-sm text-green-700 font-semibold">{ok}</p>}
    </Bloco>
  );
}

function MotivosEncerramento({ motivos, setMotivos, onErro, onOk, inp }) {
  const [novo, setNovo] = useState(null); // { rotulo, texto }
  const criar = async () => {
    if (!novo?.rotulo?.trim() || !novo?.texto?.trim()) { onErro("Preencha o nome do motivo e como ele aparece na frase."); return; }
    try {
      const m = await criarMotivoEncerramento({ rotulo: novo.rotulo, texto: novo.texto });
      setMotivos((ms) => [...ms, m]); setNovo(null); onOk("Motivo criado ✓");
    } catch (e) { onErro(e.message); }
  };
  const excluir = async (id) => {
    if (!window.confirm("Excluir este motivo?")) return;
    try { await excluirMotivoEncerramento(id); setMotivos((ms) => ms.filter((m) => m.id !== id)); } catch (e) { onErro(e.message); }
  };
  return (
    <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3">
      <h3 className="font-semibold text-sm text-gray-800">Motivos do encerramento</h3>
      <p className="text-[11px] text-gray-500 mt-0.5">
        É o que entra no lugar de <code className="bg-white px-1 rounded">{"{motivo}"}</code> e aparece na página da vaga encerrada.
        Você sempre pode escolher <em>“prefiro não informar”</em> na hora — aí nada aparece publicamente.
      </p>
      <ul className="mt-2 space-y-1 text-xs text-gray-600">
        {MOTIVOS_ENCERRAMENTO_PADRAO.map((m) => (
          <li key={m.rotulo}><strong>{m.rotulo}</strong> — “{m.texto}” <span className="text-gray-400">(do eaiRH)</span></li>
        ))}
        {motivos.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2">
            <span><strong>{m.rotulo}</strong> — “{m.texto}”</span>
            <button onClick={() => excluir(m.id)} className="text-red-600 font-semibold hover:underline">excluir</button>
          </li>
        ))}
      </ul>
      {novo ? (
        <div className="mt-2 space-y-2">
          <input className={inp} aria-label="Nome do motivo" placeholder="Nome do motivo (ex.: Cliente cancelou o projeto)"
            value={novo.rotulo} onChange={(e) => setNovo({ ...novo, rotulo: e.target.value })} />
          <input className={inp} aria-label="Como o motivo aparece na frase" placeholder="Como entra na frase (ex.: o cliente cancelou o projeto)"
            value={novo.texto} onChange={(e) => setNovo({ ...novo, texto: e.target.value })} />
          <div className="flex gap-2">
            <button onClick={criar} className="rounded-lg bg-primary text-white font-semibold px-4 py-1.5 text-sm">Criar motivo</button>
            <button onClick={() => setNovo(null)} className="rounded-lg border border-gray-300 text-gray-600 font-semibold px-3 py-1.5 text-sm hover:bg-gray-50">Cancelar</button>
          </div>
        </div>
      ) : (
        <button onClick={() => setNovo({ rotulo: "", texto: "" })} className="mt-2 text-xs font-semibold text-primary hover:underline">+ Criar meu motivo</button>
      )}
    </div>
  );
}

function TrocarSenhaBloco() {
  const [atual, setAtual] = useState("");
  const [nova, setNova] = useState("");
  const [conf, setConf] = useState("");
  const [msg, setMsg] = useState(null);
  const [erro, setErro] = useState(null);
  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-primary outline-none";
  const labelCls = "block text-sm font-semibold text-gray-700 mb-1";

  const enviar = async () => {
    setMsg(null); setErro(null);
    if (nova.length < 6) { setErro("A nova senha deve ter ao menos 6 caracteres."); return; }
    if (nova !== conf) { setErro("A confirmação não confere com a nova senha."); return; }
    if (!atual) { setErro("Informe sua senha atual para confirmar a troca."); return; }
    try {
      await trocarSenha(atual, nova);
      setMsg("Senha alterada com sucesso!");
      setAtual(""); setNova(""); setConf("");
    } catch (e) { setErro(e.message); }
  };

  return (
    <Bloco titulo="Trocar senha">
      <p className="text-xs text-gray-500 mb-2">Por segurança, confirme sua <strong>senha atual</strong> para definir uma nova. Esqueceu? Use "Esqueci minha senha" na tela de login.</p>
      {msg && <p role="status" className="mb-2 rounded-lg bg-green-50 border border-green-200 text-green-800 px-3 py-2 text-sm">{msg}</p>}
      {erro && <p role="alert" className="mb-2 rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-2 text-sm">{erro}</p>}
      <div className="mb-3"><label className={labelCls}>Senha atual</label>
        <input type="password" className={inputCls} aria-label="Senha atual" value={atual} onChange={(e) => setAtual(e.target.value)} autoComplete="current-password" placeholder="sua senha atual" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className={labelCls}>Nova senha</label>
          <input type="password" className={inputCls} aria-label="Nova senha" value={nova} onChange={(e) => setNova(e.target.value)} autoComplete="new-password" placeholder="mín. 6 caracteres" /></div>
        <div><label className={labelCls}>Confirmar nova senha</label>
          <input type="password" className={inputCls} aria-label="Confirmar nova senha" value={conf} onChange={(e) => setConf(e.target.value)} autoComplete="new-password" /></div>
      </div>
      <button onClick={enviar} disabled={!atual || !nova || !conf}
        className="mt-3 rounded-xl bg-gray-800 hover:bg-gray-900 disabled:opacity-50 text-white font-semibold px-5 py-2 text-sm">Alterar senha</button>
    </Bloco>
  );
}

function IntegracoesConta() {
  const [st, setSt] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  useEffect(() => { vagasLumeStatus().then(setSt).catch(() => setSt(null)); }, []);
  if (!st?.conectado) return null;
  const desconectar = async () => {
    if (!window.confirm("Desconectar o VagasLume? Ele deixa de receber o status das suas candidaturas.")) return;
    setOcupado(true);
    try { await vagasLumeDesconectar(); setSt({ conectado: false }); } catch (e) { alert(e.message); } finally { setOcupado(false); }
  };
  return (
    <Bloco titulo="Integrações">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <span className="text-gray-700"><b>VagasLume</b> conectado desde {new Date(st.conectadoEm).toLocaleDateString("pt-BR")}. Recebe só o status das candidaturas (nada de currículo, e-mail ou telefone).</span>
        <button disabled={ocupado} onClick={desconectar}
          className="rounded-xl border border-gray-300 px-4 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">{ocupado ? "Desconectando…" : "Desconectar"}</button>
      </div>
    </Bloco>
  );
}
